import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Gift, Clock, ArrowUpRight, MoveRight } from "lucide-react";

// Update this to the exact Consortium Diwali page URL when provided.
const CONSORTIUM_URL = "https://consortiumgifts.com/corporatediwaligifts/";

const cards = [
  { title: "The Classic Hamper", sub: "Chocolates · Dry fruits · Diya", img: "https://customer-assets-agu9un31.emergentagent.net/job_diwali-express/artifacts/veodmjjj_DSC09824.JPG-scaled.webp" },
  { title: "The Premium Box", sub: "Gourmet treats · Candle", img: "https://customer-assets-agu9un31.emergentagent.net/job_diwali-express/artifacts/4n2v90du_DSC09812.JPG-scaled.webp" },
  { title: "The Luxe Selection", sub: "For clients & partners", img: "https://customer-assets-agu9un31.emergentagent.net/job_diwali-express/artifacts/l5rpua8s_DSC09654.JPG-scaled.webp" },
  { title: "The Festive Spread", sub: "Sweets · Tea · Décor", img: "https://customer-assets-agu9un31.emergentagent.net/job_diwali-express/artifacts/ro0byd4q_DSC09797.JPG-scaled.webp" },
  { title: "The Wellness Box", sub: "Healthy · Thoughtful", img: null },
];

const ConsortiumRange = () => {
  const trackRef = useRef(null);
  const dragState = useRef({ down: false, startX: 0, scroll: 0, moved: false });
  const [grabbing, setGrabbing] = useState(false);

  const onDown = (e) => {
    const el = trackRef.current;
    if (!el) return;
    dragState.current = { down: true, startX: e.pageX, scroll: el.scrollLeft, moved: false };
    setGrabbing(true);
  };
  const onMove = (e) => {
    const el = trackRef.current;
    if (!el || !dragState.current.down) return;
    const dx = e.pageX - dragState.current.startX;
    if (Math.abs(dx) > 4) dragState.current.moved = true;
    el.scrollLeft = dragState.current.scroll - dx;
  };
  const endDrag = () => {
    dragState.current.down = false;
    setGrabbing(false);
  };
  // Prevent navigation if the user was dragging.
  const onCardClick = (e) => {
    if (dragState.current.moved) {
      e.preventDefault();
    }
  };

  return (
    <section className="relative z-10 overflow-hidden bg-[#0d1220] py-24 md:py-32" data-testid="consortium-range-section">
      <div className="pointer-events-none absolute left-0 top-0 h-96 w-96 bloom-gold opacity-30" />
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-8 max-w-3xl"
        >
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Beyond express · Powered by Consortium Gifts</span>
          <h2 className="mt-5 font-serif text-4xl leading-[1.08] tracking-tight text-[#F3EEE3] md:text-5xl">
            Explore our full range of{" "}
            <span className="italic text-[#D4AF37]">custom Diwali gifting</span>
          </h2>
          <p className="mt-5 font-sans text-base leading-relaxed text-[#A9B0C2]">
            A wide range of bespoke Diwali hampers, artisanal chocolates, premium sweets,
            décor and more — crafted for clients &amp; employees by Consortium Gifts.
          </p>
          {/* Non-express note */}
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#F3E5AB]/25 bg-[#F3E5AB]/5 px-4 py-2" data-testid="non-express-note">
            <Clock className="h-4 w-4 text-[#F3E5AB]" strokeWidth={1.6} />
            <span className="font-sans text-xs tracking-wide text-[#E8E2D0]">
              These curated ranges are made-to-order &amp; ship on standard timelines — not part of our 2-day express delivery.
            </span>
          </div>
        </motion.div>

        {/* Draggable carousel */}
        <div
          ref={trackRef}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
          className={`flex snap-x gap-5 overflow-x-auto pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${grabbing ? "cursor-grabbing" : "cursor-grab"}`}
          data-testid="consortium-carousel"
        >
          {cards.map((c, i) => (
            <a
              key={c.title}
              href={CONSORTIUM_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onCardClick}
              draggable={false}
              data-testid={`consortium-card-${i}`}
              className="group relative flex aspect-[3/4] w-[260px] shrink-0 snap-start select-none flex-col justify-end overflow-hidden rounded-2xl border border-[#F3E5AB]/10 bg-[#111826] transition-all duration-500 hover:border-[#D4AF37]/50 hover:shadow-[0_0_40px_rgba(212,175,55,0.18)] sm:w-[300px]"
            >
              {/* Image (or shimmer placeholder) */}
              {c.img ? (
                <img
                  src={c.img}
                  alt={c.title}
                  draggable={false}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              ) : (
                <div className="shimmer absolute inset-0">
                  <div className="absolute left-1/2 top-[38%] h-2/3 w-2/3 -translate-x-1/2 -translate-y-1/2 bloom-gold opacity-40 transition-opacity duration-500 group-hover:opacity-70" />
                  <div className="absolute left-1/2 top-[34%] -translate-x-1/2 -translate-y-1/2 text-[#D4AF37]/40">
                    <Gift className="h-10 w-10" strokeWidth={1} />
                  </div>
                </div>
              )}
              {/* Bottom gradient + labels */}
              <div className="relative bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/80 to-transparent p-6 pt-16">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-serif text-2xl leading-tight tracking-tight text-[#F3EEE3]">{c.title}</h3>
                    <p className="mt-1 font-sans text-sm text-[#D4AF37]">{c.sub}</p>
                  </div>
                  <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-[#F3E5AB]/60 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[#D4AF37]" />
                </div>
              </div>
            </a>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <span className="flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-[#8B93A7]">
            Drag / swipe to explore <MoveRight className="h-4 w-4" />
          </span>
          <a
            href={CONSORTIUM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="consortium-viewall-cta"
            className="inline-flex items-center gap-2 font-sans text-sm font-medium tracking-wide text-[#D4AF37] transition-colors duration-300 hover:text-[#F3E5AB]"
          >
            View full range on ConsortiumGifts.com <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ConsortiumRange;
